// Receiver-side heal: when a GC deletes a project on their side, the
// deallink__<id> shared doc is removed (see useProjectMutations.remove
// cascade). This hook scans all local projects flagged `_received === true`
// and checks whether the GC-side copy still exists.
//
// BUG 2 FIX (2026-04-22): the old version only checked
// `getDoc(shared/<_fromLink>)`. When `_fromLink` was an `estshare__` stub
// (because `_fromDealLink` was never stamped), the check missed the real
// deallink and/or looked at the wrong doc. We now check MULTIPLE sources
// and only declare the project orphaned when ALL of them fail:
//   1. shared/<_fromLink>                          (primary — deallink or estshare)
//   2. shared/<_fromDealLink> if set               (legacy)
//   3. any share doc (msgshare__, estshare__, or legacy prefixes) with
//      fromUid === project._fromUid && projName match (any existing = alive)
//
// Also: realtime-ish. Subscribes to shared docs via a 20s refetch interval
// backed by useQuery so deletion is picked up within ~20s (down from
// "whenever the user next reloads"). Also invalidates on visibilitychange
// (Shell already does this for the main appstate). Removes the
// module-level session-once guard — the hook can run repeatedly now.

import { useEffect, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { collection, getDoc, getDocs, query, where } from 'firebase/firestore'
import { db, sharedDoc } from '../lib/firebase/firestore'
import { useAuth } from './useAuth'
import { qk } from './queryKeys'
import { saveAppState } from '../lib/db'
import type { AppState } from '../types/firestore'

interface SharedLite {
  id: string
  fromUid?: string
  toUid?: string
  projName?: string
  linkId?: string
}

function normalizeName(s: string | undefined): string {
  return (s || '').trim().toLowerCase()
}

async function fetchMyShared(uid: string): Promise<SharedLite[]> {
  const col = collection(db, 'shared')
  const snap = await getDocs(query(col, where('participantUids', 'array-contains', uid)))
  const out: SharedLite[] = []
  snap.forEach((d) => {
    const data = d.data() as { fromUid?: string; toUid?: string; projName?: string; linkId?: string }
    out.push({ id: d.id, ...data })
  })
  return out
}

export function useCleanupOrphanedReceivedProjects(): void {
  const { effectiveUid: uid, isReady } = useAuth()
  const qc = useQueryClient()
  // Avoid two in-flight sweeps at once while the query is refetching.
  const inFlight = useRef(false)

  // Reuse the same subscribe/refetch window as useCleanupStaleClientCOs
  // (20s). Subscribing via useQuery means invalidations on
  // visibilitychange (handled in Shell) also trigger this.
  const { data: shared } = useQuery<SharedLite[]>({
    queryKey: ['myShared_forOrphanProjects', uid],
    queryFn: () => (uid ? fetchMyShared(uid) : Promise.resolve([])),
    enabled: !!uid && isReady,
    staleTime: 10_000,
    refetchInterval: 20_000,
  })

  useEffect(() => {
    if (!uid || !isReady) return
    if (!shared) return
    if (inFlight.current) return

    // Small delay so other heals finish first (avoid flip-flopping).
    const t = setTimeout(async () => {
      inFlight.current = true
      try {
        const state = qc.getQueryData<AppState>(qk.appstate(uid))
        if (!state) return

        // Partition shared docs into lookup structures we'll use below.
        const idToDoc = new Map<string, SharedLite>()
        for (const d of shared) idToDoc.set(d.id, d)
        // Collect docs that carry a fromUid — these are share docs (msgshare__,
        // estshare__, deallink__, and legacy prefixes). Any live share doc from
        // the GC indicates the relationship is still active.
        const shareDocsByFromUid = new Map<string, SharedLite[]>()
        for (const d of shared) {
          if (!d.fromUid) continue
          // Skip pure portallink__ docs — they don't carry fromUid in the
          // same sense and are managed separately.
          if (d.id.startsWith('portallink__')) continue
          if (d.id.startsWith('assign__')) continue
          const arr = shareDocsByFromUid.get(d.fromUid) || []
          arr.push(d)
          shareDocsByFromUid.set(d.fromUid, arr)
        }

        // Collect received projects we can verify against.
        type Candidate = {
          id: number
          name: string
          fromUid?: string
          fromLink?: string
          fromDealLink?: string
        }
        const candidates: Candidate[] = []
        for (const p of state.projects) {
          const raw = p as unknown as {
            _received?: boolean; _fromLink?: string; _fromUid?: string; _fromDealLink?: string
          }
          if (!raw._received) continue
          // Must have SOMETHING we can verify.
          if (!raw._fromLink && !raw._fromDealLink && !raw._fromUid) continue
          const c: Candidate = { id: p.id, name: p.name || '' }
          if (raw._fromUid) c.fromUid = raw._fromUid
          if (raw._fromLink) c.fromLink = raw._fromLink
          if (raw._fromDealLink) c.fromDealLink = raw._fromDealLink
          candidates.push(c)
        }
        if (candidates.length === 0) return

        const orphanProjIds: number[] = []
        for (const c of candidates) {
          // (1) Primary: check _fromLink exists (either deallink or estshare).
          let fromLinkAlive = false
          if (c.fromLink) {
            // Use the bulk snapshot first; it's fresh. Fall back to a direct
            // getDoc only if the id isn't in our snapshot — covers the edge
            // case where we aren't a participant on the doc (shouldn't happen
            // for deallinks we're part of, but be safe).
            if (idToDoc.has(c.fromLink)) {
              fromLinkAlive = true
            } else {
              try {
                const snap = await getDoc(sharedDoc(c.fromLink))
                if (snap.exists()) fromLinkAlive = true
              } catch (err) {
                // Permission / transient — err on the side of "alive" (don't
                // remove local data when we can't verify).
                console.warn('[cleanup-received-projects] verify fromLink failed', c.fromLink, err)
                fromLinkAlive = true
              }
            }
          }

          // (2) Secondary: _fromDealLink if present & different.
          let fromDealLinkAlive = false
          if (c.fromDealLink && c.fromDealLink !== c.fromLink) {
            if (idToDoc.has(c.fromDealLink)) {
              fromDealLinkAlive = true
            } else {
              try {
                const snap = await getDoc(sharedDoc(c.fromDealLink))
                if (snap.exists()) fromDealLinkAlive = true
              } catch (err) {
                console.warn('[cleanup-received-projects] verify fromDealLink failed', c.fromDealLink, err)
                fromDealLinkAlive = true
              }
            }
          }

          // (3) Fallback: any coshare/msgshare/paymshare from the sender
          // with projName matching (case-insensitive). If ANY still exists,
          // the project is alive on the sender's side.
          let shareDocAlive = false
          if (c.fromUid) {
            const docs = shareDocsByFromUid.get(c.fromUid) || []
            const wantName = normalizeName(c.name)
            for (const d of docs) {
              const dName = normalizeName(d.projName)
              // If share doc has a projName, require match. If not, treat
              // it as a legacy doc that might or might not belong — err
              // on keeping the project alive (do NOT orphan on the basis
              // of ambiguous legacy shares).
              if (!dName || dName === wantName) {
                shareDocAlive = true
                break
              }
            }
          }

          if (fromLinkAlive || fromDealLinkAlive || shareDocAlive) continue
          orphanProjIds.push(c.id)
          console.warn(
            '[cleanup-received-projects] project', c.id, '(', c.name,
            ') orphaned — no live deallink/estshare/share from', c.fromUid,
          )
        }
        if (orphanProjIds.length === 0) return

        const orphanSet = new Set(orphanProjIds)
        const inOrphans = (pid: number | undefined): boolean =>
          typeof pid === 'number' && orphanSet.has(pid)
        const next: AppState = {
          ...state,
          projects:     state.projects.filter((p) => !orphanSet.has(p.id)),
          estimates:    state.estimates.filter((e) => !inOrphans(e.projId)),
          expenses:     state.expenses.filter((e) => !inOrphans(e.projId)),
          payments:     state.payments.filter((p) => !inOrphans(p.projId)),
          schedules:    state.schedules.filter((s) => !inOrphans(s.projId)),
          changeOrders: state.changeOrders.filter((c) => !orphanSet.has(c.projId)),
          assignments:  state.assignments.filter((a) => !orphanSet.has(a.projId)),
        }

        qc.setQueryData(qk.appstate(uid), next)
        try {
          await saveAppState(uid, next)
          console.warn(
            '[cleanup-received-projects] removed', orphanProjIds.length,
            'orphaned received projects:', orphanProjIds,
          )
        } catch (err) {
          console.warn('[cleanup-received-projects] save failed', err)
        }
      } finally {
        inFlight.current = false
      }
    }, 3000)
    return () => { clearTimeout(t) }
  }, [uid, isReady, shared, qc])
}
